import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  Container,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";

import AdatisLogo from "../../../Components/Common/AdatisLogo";
import {
  getPublicClubCatalog,
  postPublicClubCatalogJoin,
  PublicClubCatalogData,
  PublicClubCatalogPlan,
} from "../../../services/publicClubCatalogService";

const formatMoneyFromCents = (cents: number, locale: string): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "BRL",
    }).format(cents / 100);
  } catch {
    return `R$ ${(cents / 100).toFixed(2)}`;
  }
};

const PublicClubPlansPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { bookingToken = "" } = useParams<{ bookingToken: string }>();

  const [data, setData] = useState<PublicClubCatalogData | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<number | "">("");
  const [name, setName] = useState("");
  const [countryCode, setCountryCode] = useState("55");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  document.title = t("public_club_plans.pageTitle");

  const load = useCallback(async (): Promise<void> => {
    if (!bookingToken) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setNotFound(false);

    try {
      const response = await getPublicClubCatalog(bookingToken);
      const payload = response.data;
      setData(payload);

      if (payload?.plans?.length === 1) {
        setSelectedPlanId(payload.plans[0].id);
      }
    } catch (err) {
      const apiError = err as { code?: string; status?: number };
      if (apiError.code === "NOT_FOUND" || apiError.status === 404) {
        setNotFound(true);
      } else {
        setError(t("public_club_plans.errors.load"));
      }
    } finally {
      setLoading(false);
    }
  }, [bookingToken, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const selectedPlan = useMemo(
    () => data?.plans?.find((plan) => plan.id === selectedPlanId),
    [data?.plans, selectedPlanId]
  );

  const canSubmit =
    selectedPlanId !== "" &&
    name.trim().length > 1 &&
    phoneNumber.replace(/\D/g, "").length >= 8 &&
    !submitting;

  const handleJoin = async (): Promise<void> => {
    if (!canSubmit || typeof selectedPlanId !== "number") {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await postPublicClubCatalogJoin(bookingToken, {
        name: name.trim(),
        country_code: countryCode.replace(/\D/g, "") || "55",
        phone_number: phoneNumber.replace(/\D/g, ""),
        client_subscription_plan_id: selectedPlanId,
      });

      const joinToken = response.data?.token;
      if (joinToken) {
        navigate(`/club/${encodeURIComponent(joinToken)}`);
        return;
      }

      setError(t("public_club_plans.errors.join"));
    } catch (err) {
      const apiError = err as { data?: string };
      setError(apiError.data || t("public_club_plans.errors.join"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page-wrapper auth-bg-cover py-5 d-flex justify-content-center align-items-center min-vh-100">
      <Container style={{ maxWidth: 560 }}>
        <div className="text-center mb-4">
          <AdatisLogo variant="light" height={36} linkTo={false} />
        </div>

        <div className="bg-white rounded shadow p-4 p-md-5">
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : notFound ? (
            <Alert color="warning" className="mb-0">
              {t("public_club_plans.notFound")}
            </Alert>
          ) : (
            <>
              <h1 className="h4 mb-2">{t("public_club_plans.title")}</h1>
              {data?.studio_name ? (
                <p className="text-muted mb-3">
                  {t("public_club_plans.subtitle", { studio: data.studio_name })}
                </p>
              ) : (
                <p className="text-muted mb-3">{t("public_club_plans.subtitleGeneric")}</p>
              )}

              {error ? (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              ) : null}

              {!data?.plans?.length ? (
                <Alert color="light" className="mb-0 border">
                  {t("public_club_plans.emptyPlans")}
                </Alert>
              ) : (
                <>
                  <div className="d-flex flex-column gap-2 mb-4">
                    {data.plans.map((plan: PublicClubCatalogPlan) => {
                      const selected = selectedPlanId === plan.id;

                      return (
                        <button
                          key={plan.id}
                          type="button"
                          className={`text-start border rounded p-3 bg-white ${
                            selected ? "border-primary" : ""
                          }`}
                          onClick={() => setSelectedPlanId(plan.id)}
                        >
                          <div className="d-flex justify-content-between gap-2">
                            <strong>{plan.name}</strong>
                            <span className="text-primary fw-semibold">
                              {formatMoneyFromCents(plan.price, i18n.language)}
                              <small className="text-muted fw-normal">
                                {" "}
                                / {t("public_club.perMonth")}
                              </small>
                            </span>
                          </div>
                          {plan.description ? (
                            <div className="small text-muted mt-1">{plan.description}</div>
                          ) : null}
                          <div className="small text-muted mt-1">
                            {plan.monthly_visit_limit != null
                              ? t("public_club.visitLimit", {
                                  limit: plan.monthly_visit_limit,
                                })
                              : t("public_club.visitUnlimited")}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  <FormGroup>
                    <Label htmlFor="club-plans-name">{t("public_club_plans.fields.name")}</Label>
                    <Input
                      id="club-plans-name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      autoComplete="name"
                    />
                  </FormGroup>
                  <div className="row g-2">
                    <div className="col-4">
                      <FormGroup>
                        <Label htmlFor="club-plans-ddi">{t("public_club_plans.fields.ddi")}</Label>
                        <Input
                          id="club-plans-ddi"
                          value={countryCode}
                          onChange={(e) => setCountryCode(e.target.value)}
                          inputMode="numeric"
                        />
                      </FormGroup>
                    </div>
                    <div className="col-8">
                      <FormGroup>
                        <Label htmlFor="club-plans-phone">
                          {t("public_club_plans.fields.phone")}
                        </Label>
                        <Input
                          id="club-plans-phone"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          inputMode="tel"
                          autoComplete="tel"
                        />
                      </FormGroup>
                    </div>
                  </div>
                  <p className="small text-muted">{t("public_club_plans.lgpdHint")}</p>
                  <div className="d-grid">
                    <Button
                      color="primary"
                      size="lg"
                      disabled={!canSubmit}
                      onClick={() => {
                        void handleJoin();
                      }}
                    >
                      {submitting ? (
                        <Spinner size="sm" />
                      ) : selectedPlan ? (
                        t("public_club_plans.continueWithPrice", {
                          price: formatMoneyFromCents(selectedPlan.price, i18n.language),
                        })
                      ) : (
                        t("public_club_plans.continue")
                      )}
                    </Button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </Container>
    </div>
  );
};

export default PublicClubPlansPage;
